import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Collapse,
  Stack,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Select,
  MenuItem,
  FormControl
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { getSessions, getAdminUsers, getAdminQuotes, updateQuoteStatus } from "../api/apiClient";
import { getTokenPayload } from "../api/session";

const CopyableSection = ({ title, content }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" fontWeight="bold">{title}</Typography>
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
          {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Paper variant="outlined" sx={{ p: 1, maxHeight: 60, overflow: 'auto', bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{content}</Typography>
      </Paper>
    </Box>
  );
};

const getStatusColor = (status) => {
  switch (status?.toUpperCase()) {
    case "PASS":
      return { bg: "#e8f5e9", color: "#2e7d32" }; // GREEN
    case "FAIL":
      return { bg: "#ffebee", color: "#c62828" }; // RED
    case "WARNING":
    case "WARNING / NEEDS REVIEW":
      return { bg: "#fff3e0", color: "#ef6c00" }; // YELLOW
    case "NOT RUN":
      return { bg: "#f5f5f5", color: "#9e9e9e" }; // GRAY
    case "BLOCKED":
      return { bg: "#212121", color: "#ffffff" }; // BLACK
    default:
      return { bg: "#f5f5f5", color: "#9e9e9e" };
  }
};

/**
 * One row of the admin sessions monitor.
 *
 * Metadata only, by design. Users' saved projects are private to them, so the
 * API withholds the request text and the generated blueprint from this feed —
 * admins get who / when / outcome for operational monitoring, not the work
 * itself.
 */
const SessionRow = ({ session }) => {
  const statusColors = getStatusColor(session.status);

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: "monospace" }} title={session.session_id}>
          {session.session_id.includes("_")
            ? session.session_id.split("_").slice(0, 2).join("_")
            : session.session_id.split("-")[0] + "…"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          fontWeight={600}
          color={session.user_name && session.user_name !== "Guest" ? "primary.main" : "text.secondary"}
        >
          {session.user_name || "Guest"}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={session.user_id ? "Registered" : "Guest"}
          size="small"
          variant="outlined"
          color={session.user_id ? "primary" : "default"}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={session.status}
          size="small"
          sx={{
            bgcolor: statusColors.bg,
            color: statusColors.color,
            fontWeight: 700,
            borderRadius: 1
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary">
          {session.created_at ? new Date(session.created_at).toLocaleString() : "—"}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" color="text.secondary">
          {session.updated_at ? new Date(session.updated_at).toLocaleString() : "—"}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export default function DashboardPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("Guest");
  const [activeTab, setActiveTab] = useState(0);

  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminQuotes, setAdminQuotes] = useState([]);
  const [adminQuotesLoading, setAdminQuotesLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 1 && adminUsers.length === 0 && !adminUsersLoading) {
      fetchAdminUsers();
    }
    if (activeTab === 2 && adminQuotes.length === 0 && !adminQuotesLoading) {
      fetchAdminQuotes();
    }
  }, [activeTab]);

  const fetchAdminQuotes = async () => {
    setAdminQuotesLoading(true);
    try {
      const data = await getAdminQuotes();
      setAdminQuotes(data.quotes || []);
    } catch (err) {
      console.error("Failed to fetch admin quotes", err);
    } finally {
      setAdminQuotesLoading(false);
    }
  };

  const handleQuoteStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteStatus(quoteId, newStatus);
      setAdminQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
    } catch (err) {
      console.error("Failed to update quote status", err);
      alert("Failed to update status");
    }
  };

  const userStats = useMemo(() => {
    const total = adminUsers.length;
    let subscribed = 0;

    adminUsers.forEach(u => {
      if (u.build_credits > 0 || (u.purchases && u.purchases.length > 0)) {
        subscribed++;
      }
    });

    const free = total - subscribed;
    return { total, subscribed, free };
  }, [adminUsers]);

  const quoteStats = useMemo(() => {
    return {
      pending: adminQuotes.filter(q => q.status === 'pending' || !q.status).length,
      processing: adminQuotes.filter(q => q.status === 'processing').length,
      completed: adminQuotes.filter(q => q.status === 'completed').length,
      deny: adminQuotes.filter(q => q.status === 'deny').length,
    };
  }, [adminQuotes]);

  const fetchAdminUsers = async () => {
    setAdminUsersLoading(true);
    try {
      const data = await getAdminUsers();
      setAdminUsers(data.users || []);
    } catch (err) {
      console.error("Failed to fetch admin users", err);
    } finally {
      setAdminUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();

    // Check login status
    const payload = getTokenPayload();
    if (payload?.name) {
      setUserName(payload.name);
    }
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const data = await getSessions();
      setSessions(data.sessions || []);
      setError("");
    } catch (err) {
      console.error("Failed to fetch sessions", err);
      setError("Failed to load sessions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    pass: sessions.filter(s => s.status === 'PASS').length,
    fail: sessions.filter(s => s.status === 'FAIL').length,
    warning: sessions.filter(s => s.status === 'WARNING').length,
    blocked: sessions.filter(s => s.status === 'BLOCKED').length,
    notRun: sessions.filter(s => s.status === 'NOT RUN').length,
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", pb: 6, px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h4" component="h1" color="primary.dark" gutterBottom>
            Dashboard
          </Typography>
          {/* <Typography variant="subtitle1" color="primary.main" fontWeight="bold">
            Welcome, {userName}!
          </Typography> */}
        </Box>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Monitor all intent validations and their outcomes across different sessions.
      </Typography>

      <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />} sx={{ mb: 4 }}>
        Users&apos; saved projects are private to them. This console shows session
        outcomes and account activity only — not the request text or the
        generated blueprints.
      </Alert>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Sessions Monitoring" sx={{ fontWeight: 600 }} />
        <Tab label="Admin Panel" sx={{ fontWeight: 600 }} />
        <Tab label="Expert Support Requests" sx={{ fontWeight: 600 }} />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #2e7d32' }}>
                <Typography variant="h4" color="#2e7d32" fontWeight={700}>{stats.pass}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>PASSED</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #c62828' }}>
                <Typography variant="h4" color="#c62828" fontWeight={700}>{stats.fail}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>FAILED</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #ef6c00' }}>
                <Typography variant="h4" color="#ef6c00" fontWeight={700}>{stats.warning}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>WARNINGS</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #212121' }}>
                <Typography variant="h4" color="#212121" fontWeight={700}>{stats.blocked}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>BLOCKED</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #9e9e9e' }}>
                <Typography variant="h4" color="#9e9e9e" fontWeight={700}>{stats.notRun}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>NOT RUN</Typography>
              </Paper>
            </Grid>
          </Grid>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              action={
                <IconButton size="small" color="inherit" onClick={fetchSessions} title="Retry">
                  <ReplayIcon fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} elevation={2} sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: { xs: 300, md: 650 } }} aria-label="sessions table">
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Session ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">No sessions found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sessions.map((session) => <SessionRow key={session.session_id} session={session} />)
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" color="primary.dark" fontWeight={600}>
              User Management
            </Typography>
            <Chip label="Admin Access" color="secondary" />
          </Box>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #1976d2' }}>
                <Typography variant="h4" color="primary.main" fontWeight={700}>{userStats.total}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Total Users</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #2e7d32' }}>
                <Typography variant="h4" color="#2e7d32" fontWeight={700}>{userStats.subscribed}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Subscribed Users</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #9e9e9e' }}>
                <Typography variant="h4" color="#9e9e9e" fontWeight={700}>{userStats.free}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Free Users</Typography>
              </Paper>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={2} sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: { xs: 300, md: 650 } }}>
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name / Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Subscription details</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Purchase History</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminUsersLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : adminUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">No users found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  adminUsers.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{u.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "N/A"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">Free Attempts: {u.free_blueprint_attempts}</Typography>
                        <Typography variant="body2">Build Credits: {u.build_credits}</Typography>
                        {(u.build_credits > 0 || (u.purchases && u.purchases.length > 0)) && (
                          <Chip size="small" label="Subscribed" color="success" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {u.purchases && u.purchases.length > 0 ? (
                          <Stack spacing={1}>
                            {u.purchases.map(p => (
                              <Box key={p.id} sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight={600} display="block">
                                  {p.product_type} - ${(p.amount / 100).toFixed(2)} {p.currency.toUpperCase()}
                                </Typography>
                                <Typography variant="caption" color={p.status === 'complete' ? "success.main" : "text.secondary"}>
                                  {p.status} on {new Date(p.created_at).toLocaleDateString()}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">No purchases</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" color="primary.dark" fontWeight={600}>
              Expert Support Requests
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #757575' }}>
                <Typography variant="h4" color="text.secondary" fontWeight={700}>{quoteStats.pending}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Pending</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #ef6c00' }}>
                <Typography variant="h4" color="#ef6c00" fontWeight={700}>{quoteStats.processing}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Processing</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #2e7d32' }}>
                <Typography variant="h4" color="#2e7d32" fontWeight={700}>{quoteStats.completed}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Completed</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderTop: '4px solid #c62828' }}>
                <Typography variant="h4" color="#c62828" fontWeight={700}>{quoteStats.deny}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>Deny</Typography>
              </Paper>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={2} sx={{ overflowX: "auto", mb: 4 }}>
            <Table sx={{ minWidth: { xs: 300, md: 650 } }}>
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Requester</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contact Info</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Submitted Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Context</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminQuotesLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : adminQuotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">No support requests found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  adminQuotes.map((q) => (
                    <TableRow key={q.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{q.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{q.email}</Typography>
                        {q.phone && <Typography variant="caption" color="text.secondary" display="block">{q.phone}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{q.created_at ? new Date(q.created_at).toLocaleString() : "N/A"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1} sx={{ maxWidth: 400 }}>
                          {q.query && (
                            <CopyableSection title="Query:" content={q.query} />
                          )}
                          {q.blueprint && (
                            <CopyableSection title="Blueprint:" content={q.blueprint} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={q.status || "pending"}
                            onChange={(e) => handleQuoteStatusChange(q.id, e.target.value)}
                            sx={{
                              bgcolor: q.status === 'completed' ? '#e8f5e9' : 
                                       q.status === 'deny' ? '#ffebee' : 
                                       q.status === 'processing' ? '#fff3e0' : '#f5f5f5',
                              color: q.status === 'completed' ? '#2e7d32' : 
                                     q.status === 'deny' ? '#c62828' : 
                                     q.status === 'processing' ? '#ef6c00' : '#757575',
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            }}
                          >
                            <MenuItem value="pending" sx={{ color: '#757575', fontWeight: 600 }}>Pending</MenuItem>
                            <MenuItem value="processing" sx={{ color: '#ef6c00', fontWeight: 600 }}>Processing</MenuItem>
                            <MenuItem value="completed" sx={{ color: '#2e7d32', fontWeight: 600 }}>Completed</MenuItem>
                            <MenuItem value="deny" sx={{ color: '#c62828', fontWeight: 600 }}>Deny</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
